import cx from 'classnames'
import { TFunction } from 'i18next'
import { buttonText } from '../../../../../utils/ec-flow-texts'
import { resolveDisplayName } from '../../../../../../../server/src/utils'
import { EndUserCampaign, PartialEndUser } from '../../../../../lib/api/types'
import { endUserType } from '../../../../../utils/user-type'
import { Button } from '@alphabait/ui'
import { displayS3File } from '../../../../../../../client/src/utils/s3'
import type { ResolvedEndUserTheme } from '../../../../../../../server/src/customer/end-user-theme.schema'
import type { CSSProperties } from 'react'

/** The home greeting's colour; unset keeps the text colour. */
export function homeGreetingStyle(
  theme?: ResolvedEndUserTheme | null,
): CSSProperties | undefined {
  return theme?.colorGreeting ? { color: theme.colorGreeting } : undefined
}

/**
 * The home headline: the theme's heading colour and heading font. With a heading
 * font the headline takes the heaviest weight uploaded for it, so the browser never
 * fakes a bold from a single Medium file. Unset keeps today's look.
 */
export function homeHeadingStyle(
  theme?: ResolvedEndUserTheme | null,
): CSSProperties | undefined {
  if (!theme) return undefined
  const weights = theme.headingFontFiles.map((file) => Number(file.weight))
  return {
    ...(theme.colorHeading && { color: theme.colorHeading }),
    ...(theme.headingFontFamily && {
      fontFamily: `'${theme.headingFontFamily}', var(--font-family-sans)`,
      ...(weights.length > 0 && { fontWeight: Math.max(...weights) }),
    }),
  }
}

/**
 * Home with a hero photo on the top two thirds, the customer's logo over it, then
 * headline and button. Gradients use the theme background so the photo blends
 * into the page. The photo must not contain the logo; the logo is its own element.
 */
export default function HomeHero({
  campaign,
  endUser,
  id,
  t,
  pathname,
  navigate,
}: {
  campaign: EndUserCampaign
  endUser: PartialEndUser
  t: TFunction<'translation', undefined>
  pathname: string
  id?: string
  navigate: (path: string) => void
}) {
  const language = 'nl'
  const typeOfUser = endUserType(endUser.referral)
  const theme = campaign.customer?.endUserTheme
  // The theme's screen logo when it has one, else the logo this campaign uploaded.
  const logo =
    campaign.customer?.endUserTheme?.logoUrl ??
    (campaign.logo ? displayS3File(campaign.logo) : null)

  const salutation = campaign.salutation as
    | { salutationType?: string; nameFormat?: string }
    | undefined
  const translations =
    campaign?.ecFlowTexts?.ecFlowTexts?.[typeOfUser]?.['home']?.[language]

  const isReferral =
    endUser?.referralOf && endUser?.referralOf != endUser.campaignId

  const formattedName = resolveDisplayName(salutation, {
    firstName: endUser.firstName || '',
    middle: endUser.middle || '',
    lastName: endUser.lastName || '',
  })
  const customSalutation =
    salutation?.salutationType && salutation?.nameFormat
      ? `${salutation.salutationType}${
          formattedName ? ` ${formattedName}` : ''
        },`
      : undefined

  const endUserSalutation = isReferral
    ? ''
    : campaign.postcardTemplate?.['type'] === 'flyer'
    ? `${t('ec.hello')},`
    : customSalutation || `${t('ec.dear')} ${endUser?.firstName},`

  return (
    <div
      className={cx(
        pathname.includes('campaigns')
          ? 'cursor-none pointer-events-none'
          : 'w-full',
        'flex flex-col bg-background text-foreground-light relative max-w-md mx-auto',
      )}
    >
      {/* reason: 65% of the screen, not of the page. A percent would grow with the text; --sh is 1cqh in the admin phone preview. */}
      <div className="relative w-full h-[calc(65*var(--sh,1svh))] shrink-0">
        <img
          src={theme?.heroImageUrl ?? undefined}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-center bg-background"
        />

        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: '110px',
            background:
              'linear-gradient(180deg, var(--color-background) 0%, transparent 100%)',
            zIndex: 5,
          }}
        />

        <div
          className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{
            height: '110px',
            background:
              'linear-gradient(180deg, transparent 0%, color-mix(in srgb, var(--color-background) 70%, transparent) 60%, var(--color-background) 100%)',
            zIndex: 5,
          }}
        />

        {logo && (
          <div className="absolute top-2 left-0 right-0 flex items-center justify-center px-6 py-4 z-30">
            <img
              src={logo}
              className="max-h-20 max-w-70 w-auto object-contain"
              alt="Logo"
            />
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col px-6 pb-8">
        <p
          className="mb-2 font-weight-light text-3xl"
          style={homeGreetingStyle(theme)}
        >
          {endUserSalutation}
        </p>

        <h2
          className={cx(
            'text-4xl font-weight-bold mb-4 pb-2',
            theme?.headingUppercase !== false && 'uppercase',
          )}
          style={homeHeadingStyle(theme)}
        >
          {translations?.header ?? t('ec.home.header')}
        </h2>

        <div
          className="mb-8 font-weight-light leading-6 text-xl"
          dangerouslySetInnerHTML={{
            __html: translations?.text ?? t('ec.home.text'),
          }}
        />

        <Button
          variant="next"
          size="lg"
          className="w-full rounded-lg py-4 text-base font-medium"
          onClick={() => navigate(`/${id}/choose-incentive`)}
        >
          {buttonText(campaign, 'view', t)}
        </Button>
      </div>
    </div>
  )
}
